import styles from './Checkout.module.css';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useState, useEffect, useRef, forwardRef, useCallback } from 'react';

import { processPayment } from '../../api';

// MARK: Order Summary Component
const OrderSummary = ({ items, selectedPaymentMethod, isPatroller }) => {
    const ITEM_FEE = 2.00; // $2 per item listing fee

    // Separate donation items from regular items
    const regularItems = items.filter(item => !item.donation);
    const donationItems = items.filter(item => item.donation);

    const subtotal = regularItems.reduce((sum, item) => sum + ((item.quantity || 1) * ITEM_FEE), 0);
    const discount = isPatroller ? subtotal : 0;
    const donations = donationItems.length * ITEM_FEE;
    const total = Math.max(subtotal - discount - donations, 0);

    return (
        <Card>
            <CardHeader title="Order Summary" />
            <CardContent>
                {/* Itemized breakdown */ }
                <Typography variant="h6" sx={ { mb: 2 } }>Items</Typography>

                {/* Regular items with detailed fees */ }
                { regularItems.map((item, index) => {
                    const quantity = item.quantity || 1;
                    const itemTotal = quantity * ITEM_FEE;

                    return (
                        <div key={ item.id || index } className={ styles.orderItem }>
                            <div>
                                <Typography variant="body1">{ item.name || `Item ${ index + 1 }` }</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    $ { ITEM_FEE.toFixed(2) } x { quantity } { quantity > 1 ? 'items' : 'item' }
                                </Typography>
                            </div>

                            <Typography variant="body1">${ itemTotal.toFixed(2) }</Typography>
                        </div>
                    );
                }) }

                {/* Donation items */ }
                { donationItems.map((item, index) => (
                    <div key={ item.id || index } className={ styles.orderItem }>
                        <div>
                            <Typography variant="body1">{ item.name || `Donation Item ${ index + 1 }` }</Typography>
                            <Typography variant="caption" color="primary">Donation - No listing fee</Typography>
                        </div>

                        <Typography variant="body1" color="primary">$0.00</Typography>
                    </div>
                )) }

                <Divider sx={ { my: 2 } } />

                {/* Calculation breakdown */ }
                <Typography variant="h6" sx={ { mb: 1 } }>Calculation</Typography>

                {/* Listing fees subtotal */ }
                { regularItems.length > 0 && (
                    <div className={ styles.calculationRow }>
                        <Typography variant="body2">
                            Listing fees ({ regularItems.length } item{ regularItems.length !== 1 ? 's' : '' } x ${ ITEM_FEE.toFixed(2) })
                        </Typography>

                        <Typography variant="body2">${ subtotal.toFixed(2) }</Typography>
                    </div>
                ) }

                <Divider sx={ { my: 2 } } />

                {/* Subtotal before discounts */ }
                <div className={ styles.calculationRow }>
                    <Typography variant="h6" sx={ { fontWeight: 'bold' } }>Subtotal</Typography>
                    <Typography variant="h6" sx={ { fontWeight: 'bold' } }>${ subtotal.toFixed(2) }</Typography>
                </div>

                <Divider sx={ { my: 2 } } />

                {/* Patroller discount */ }
                { isPatroller && (
                    <div className={ styles.calculationRow }>
                        <Typography variant="h6" sx={ { fontWeight: 'bold' } }>Patroller Discount</Typography>
                        <Typography variant="h6" sx={ { fontWeight: 'bold' } } color="success">-${ discount.toFixed(2) }</Typography>
                    </div>
                ) }

                { isPatroller && <Divider sx={ { my: 2 } } /> }

                {/* Final total */ }
                <div className={ styles.orderTotal }>
                    <Typography variant="h5" sx={ { fontWeight: 'bold' } }>
                        Total: ${ total.toFixed(2) }
                    </Typography>

                    { selectedPaymentMethod === 'cash' && (
                        <Typography variant="caption" color="text.secondary" sx={ { mt: 1 } }>
                            All fees due at item drop-off.
                        </Typography>
                    ) }
                </div>
            </CardContent>
        </Card>
    );
};

// MARK: Card Input Component (Square)
const CardInput = forwardRef(({ onCardTokenized, onError }, ref) => {
    const [ card, setCard ] = useState(null);

    useEffect(() => {
        const initializeSquare = async () => {
            // Wait for DOM to be ready
            await new Promise(resolve => setTimeout(resolve, 100));

            if (!window.Square) {
                console.error('Square SDK not loaded');

                onError('Payment system not available');

                return;
            }

            // Check if required environment variables are set
            const squareAppId = process.env.REACT_APP_SQUARE_APP_ID;
            const squareLocationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

            if (!squareAppId) {
                console.error('Square environment variables not configured');

                onError('Payment system not configured. Please contact support.');

                return;
            }

            // Verify the card container exists
            const cardContainer = document.getElementById('card-container');
            if (!cardContainer) {
                console.error('Card container element not found');

                onError('Payment form element not found');

                return;
            }

            // Clear any existing content before attaching new form
            cardContainer.innerHTML = '';

            try {
                const payments = window.Square.payments(squareAppId, squareLocationId);
                const cardInstance = await payments.card();

                await cardInstance.attach('#card-container');

                setCard(cardInstance);
            } catch (error) {
                console.error('Failed to initialize Square card:', error);

                onError('Failed to initialize payment form');
            }
        };

        // Check if script is already loading or loaded
        const existingScript = document.querySelector('script[src="' + process.env.REACT_APP_SQUARE_SDK + '"]');

        if (!window.Square && !existingScript) {
            const script = document.createElement('script');

            script.src = process.env.REACT_APP_SQUARE_SDK;
            script.onload = initializeSquare;
            script.onerror = () => onError('Failed to load payment system');

            document.head.appendChild(script);
        } else if (window.Square) {
            initializeSquare();
        }

        return () => {
            if (card) {
                card.destroy();
            }
        };
    }, [ card, onError, ]); // Remove onError from dependencies to prevent re-initialization

    const tokenizeCard = useCallback(async () => {
        if (!card) {
            throw new Error('Card form not initialized');
        }

        const tokenResult = await card.tokenize();
        if (tokenResult.status === 'OK') {
            onCardTokenized(tokenResult.token);

            return tokenResult.token;
        } else {
            throw new Error(tokenResult.errors?.[ 0 ]?.message || 'Card tokenization failed');
        }
    }, [ card, onCardTokenized ]);

    // Expose tokenize method to parent
    useEffect(() => {
        if (ref) {
            ref.current = { tokenizeCard };
        }
    }, [ card, ref, tokenizeCard ]);

    return (
        <Card>
            <CardHeader title="Card Information" />
            <CardContent>
                <div id="card-container" style={ { minHeight: '100px' } }></div>
                <Typography variant="caption" color="text.secondary" sx={ { mt: 1 } }>
                    Your card information is secure and processed by Square.
                </Typography>
            </CardContent>
        </Card>
    );
});

CardInput.displayName = 'CardInput';

// MARK: Payment Method Selection
const PaymentMethods = ({ paymentMethods, selectedMethod, onMethodChange }) => {
    return (
        <Card>
            <CardHeader title="Payment Method" />

            <CardContent>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Select Payment Method</FormLabel>

                    <RadioGroup
                        value={ selectedMethod }
                        onChange={ (event, value) => onMethodChange(value) }
                    >
                        { paymentMethods.map((method) => (
                            <FormControlLabel
                                key={ method.id }
                                value={ method.id }
                                control={ <Radio /> }
                                label={ method.name }
                            />
                        )) }
                    </RadioGroup>
                </FormControl>
            </CardContent>
        </Card>
    );
};

// MARK: Billing Information
const BillingInfo = ({ billingInfo, onBillingChange }) => {
    const handleChange = (field) => (event) => {
        onBillingChange({
            ...billingInfo,
            [ field ]: event.target.value
        });
    };

    return (
        <Card>
            <CardHeader title="Billing Information" />

            <CardContent>
                <Grid container spacing={ 2 }>
                    <Grid size={ { xs: 12, sm: 6 } }>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={ billingInfo.firstName || '' }
                            onChange={ handleChange('firstName') }
                            required
                        />
                    </Grid>

                    <Grid size={ { xs: 12, sm: 6 } }>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={ billingInfo.lastName || '' }
                            onChange={ handleChange('lastName') }
                            required
                        />
                    </Grid>

                    <Grid size={ 12 }>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={ billingInfo.email || '' }
                            onChange={ handleChange('email') }
                            required
                        />
                    </Grid>

                    <Grid size={ 12 }>
                        <TextField
                            fullWidth
                            label="Address"
                            value={ billingInfo.address || '' }
                            onChange={ handleChange('address') }
                            required
                        />
                    </Grid>

                    <Grid size={ { xs: 12, sm: 6 } }>
                        <TextField
                            fullWidth
                            label="City"
                            value={ billingInfo.city || '' }
                            onChange={ handleChange('city') }
                            required
                        />
                    </Grid>

                    <Grid size={ { xs: 12, sm: 3 } }>
                        <TextField
                            fullWidth
                            label="State"
                            value={ billingInfo.state || '' }
                            onChange={ handleChange('state') }
                            required
                        />
                    </Grid>

                    <Grid size={ { xs: 12, sm: 3 } }>
                        <TextField
                            fullWidth
                            label="ZIP Code"
                            value={ billingInfo.zipCode || '' }
                            onChange={ handleChange('zipCode') }
                            required
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// MARK: Main Checkout Component
export const Checkout = ({
    items = [],
    paymentMethods = [
        { id: 'card', name: 'Credit/Debit Card' },
        { id: 'cash', name: 'Cash' }
    ],
    isPatroller,
    onPaymentSuccess,
    onCancel,
    token // Firebase auth token for API calls
}) => {
    const [ selectedPaymentMethod, setSelectedPaymentMethod ] = useState('');
    const [ billingInfo, setBillingInfo ] = useState({});
    const [ isProcessing, setIsProcessing ] = useState(false);
    const [ error, setError ] = useState(null);

    const cardInputRef = useRef(null);

    // Calculate totals
    const ITEM_FEE = 2.00; // $2 per item fee

    const regularItems = items.filter(item => !item.donation);
    const donationItems = items.filter(item => item.donation);
    const subtotal = regularItems.reduce((sum, item) => sum + ((item.quantity || 1) * (isPatroller ? 0 : ITEM_FEE)), 0);
    const discount = isPatroller ? subtotal : 0;
    const donations = donationItems.length * ITEM_FEE;
    const total = Math.max(subtotal - discount - donations, 0);

    // Filter payment methods based on total
    const availablePaymentMethods = total > 0 ? paymentMethods : paymentMethods.filter(method => method.id === 'cash');

    const handlePayment = async () => {
        // For patrollers, complete immediately without payment
        if (isPatroller) {
            const patrollerSuccessResult = {
                paymentMethod: 'patroller',
                status: 'COMPLETED',
                amount: 0,
                items: items,
                patrollerDiscount: true
            };
            onPaymentSuccess && onPaymentSuccess(patrollerSuccessResult);
            return;
        }

        if (!selectedPaymentMethod) {
            setError('Please select a payment method');
            return;
        }

        // Basic validation - skip billing info for cash payments
        if (selectedPaymentMethod !== 'cash') {
            const requiredFields = [ 'firstName', 'lastName', 'email', 'address', 'city', 'state', 'zipCode' ];
            const missingFields = requiredFields.filter(field => !billingInfo[ field ]);

            if (missingFields.length > 0) {
                setError(`Please fill in all required fields: ${ missingFields.join(', ') }`);
                return;
            }
        }

        setIsProcessing(true);
        setError(null);

        try {
            let paymentToken = null;

            // Handle cash payments immediately without backend processing
            if (selectedPaymentMethod === 'cash') {
                const cashSuccessResult = {
                    paymentMethod: 'cash',
                    cashInstructions: 'You can pay your listing fees with cash when dropping off your items for the ski swap. The items must be dropped off on Friday, Nov 7 between 5:00pm and 8:00pm or on Saturday, Nov 8 between 8:00am and 10:00am.',
                    status: 'PENDING_CASH',
                    amount: total,
                    items: items
                };
                onPaymentSuccess && onPaymentSuccess(cashSuccessResult);
                return;
            }

            // Tokenize card if credit card payment
            if (!cardInputRef.current) {
                throw new Error('Card form not available');
            }

            paymentToken = await cardInputRef.current.tokenizeCard();

            // This is where payment processing would happen
            // The actual implementation depends on the payment service
            const paymentData = {
                items,
                total,
                paymentMethod: selectedPaymentMethod,
                billingInfo,
                cardToken: paymentToken
            };

            // Use the API function to process payment
            const result = await processPayment(paymentData, token);

            // Check for successful card payment
            if (result && result.paymentId) {
                const successStatuses = [ 'COMPLETED' ];

                if (successStatuses.includes(result.status)) {
                    onPaymentSuccess && onPaymentSuccess(result);
                } else {
                    throw new Error(`Payment status '${ result.status }' not recognized as successful`);
                }
            } else {
                throw new Error(result?.error || 'Payment failed - no payment ID returned');
            }
        } catch (err) {
            const errorMessage = err.message || 'Payment processing failed';

            setError(errorMessage);

            // Error is handled locally - don't bubble up to parent
        } finally {
            setIsProcessing(false);
        }
    };

    // Ensure selected payment method is valid
    useEffect(() => {
        if (availablePaymentMethods.length > 0 && !availablePaymentMethods.find(method => method.id === selectedPaymentMethod)) {
            setSelectedPaymentMethod(availablePaymentMethods[ 0 ].id);
        }

        if (!selectedPaymentMethod) {
            if (isPatroller) {
                setSelectedPaymentMethod('cash');
            }
        }
    }, [ availablePaymentMethods, selectedPaymentMethod, isPatroller ]);

    return (
        <div className={ styles.checkoutContainer }>
            <Typography variant="h4" gutterBottom>
                Checkout
            </Typography>

            { error && (
                <Alert
                    severity="error"
                    sx={ { mb: 2 } }
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={ () => {
                                setError(null);
                                // Optionally re-attempt payment automatically
                                // handlePayment();
                            } }
                        >
                            Retry
                        </Button>
                    }
                >
                    { error }
                </Alert>
            ) }

            <Grid container spacing={ 3 } sx={ { width: '100%' } }>
                { !isPatroller && (
                    <Grid size={ { xs: 12, md: 8 } }>
                        <Grid container spacing={ 3 }>
                            { selectedPaymentMethod === 'card' && (
                                <Grid size={ 12 }>
                                    <BillingInfo
                                        billingInfo={ billingInfo }
                                        onBillingChange={ setBillingInfo }
                                    />
                                </Grid>
                            ) }

                            <Grid size={ { xs: 12, sm: 6 } }>
                                <PaymentMethods
                                    paymentMethods={ availablePaymentMethods }
                                    selectedMethod={ selectedPaymentMethod }
                                    onMethodChange={ setSelectedPaymentMethod }
                                />
                            </Grid>

                            { selectedPaymentMethod === 'card' && (
                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <CardInput
                                        ref={ cardInputRef }
                                        onCardTokenized={ () => { } } // Token is handled in tokenizeCard method
                                        onError={ setError }
                                    />
                                </Grid>
                            ) }
                        </Grid>
                    </Grid>
                ) }

                { isPatroller && ( // spacer for layout consistency
                    <Grid size={ 3 } />
                ) }

                <Grid size={ { xs: 12, md: isPatroller ? 6 : 4 } } >
                    <OrderSummary
                        key={ `order-summary-${ isPatroller }` }
                        items={ items }
                        selectedPaymentMethod={ selectedPaymentMethod }
                        isPatroller={ isPatroller }
                    />

                    <Stack spacing={ 2 } sx={ { mt: 2 } } direction='row' justifyContent='flex-end'>
                        <Button
                            variant="outlined"
                            onClick={ onCancel }
                            disabled={ isProcessing }
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={ handlePayment }
                            disabled={ isProcessing || items.length === 0 || (!isPatroller && !selectedPaymentMethod) }
                            sx={ { mt: 1 } }
                            loading={ isProcessing }
                            loadingIndicator="Processing..."
                            loadingPosition="start"
                        >
                            { total === 0 ? 'Complete Listing' :
                                selectedPaymentMethod === 'cash' ? 'Pay w/cash Onsite' : `Pay $${ total.toFixed(2) }`
                            }
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </div>
    );
};